import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Clock } from 'lucide-react';

const SESSION_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours
const WARNING_THRESHOLD_MS = 2 * 60 * 1000;       // banner shows at T-2min
const POLL_INTERVAL_MS = 15 * 1000;                // re-check every 15s

const SESSION_KEY = 'session_expires_at';

/**
 * Sets the session expiry to 24 hours from now. Call after login.
 */
export function startSessionTimer() {
  localStorage.setItem(SESSION_KEY, String(Date.now() + SESSION_DURATION_MS));
}

/**
 * Clear any tracked session expiry. Call on logout.
 */
export function clearSessionTimer() {
  localStorage.removeItem(SESSION_KEY);
}

function formatRemaining(ms) {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Shows a sticky banner 2 minutes before the 24h session expires, letting the
 * user extend with one click. If the user does nothing, they're auto-logged-out
 * at expiry; the API 401 interceptor handles redirect-to-login with a toast.
 */
export default function SessionTimeoutBanner() {
  const { user, logout } = useAuth();
  const [remaining, setRemaining] = useState(null);
  const lastCheckRef = useRef(0);

  useEffect(() => {
    if (!user) {
      setRemaining(null);
      return;
    }
    // Initialize timer if not set (e.g., for users already logged in before this feature shipped)
    if (!localStorage.getItem(SESSION_KEY)) startSessionTimer();

    let cancelled = false;
    const check = () => {
      if (cancelled) return;
      const expires = parseInt(localStorage.getItem(SESSION_KEY) || '0', 10);
      const remain = expires - Date.now();
      if (remain <= 0) {
        // Expired — log out cleanly. The 401 interceptor will handle the redirect+toast
        // for any inflight request; we also force it here in case the user is idle.
        clearSessionTimer();
        logout();
        const path = window.location.pathname;
        if (!path.startsWith('/login') && !path.startsWith('/signup')) {
          localStorage.setItem('return_to', path + window.location.search);
          window.location.href = '/login?expired=1';
        }
        return;
      }
      if (remain <= WARNING_THRESHOLD_MS) setRemaining(remain);
      else setRemaining(null);
    };
    check();
    const id = setInterval(check, POLL_INTERVAL_MS);

    // Also tick down the visible counter every second when the banner is showing
    const tick = setInterval(() => {
      const expires = parseInt(localStorage.getItem(SESSION_KEY) || '0', 10);
      const remain = expires - Date.now();
      if (remain <= WARNING_THRESHOLD_MS && remain > 0) setRemaining(remain);
    }, 1000);

    return () => { cancelled = true; clearInterval(id); clearInterval(tick); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const stayIn = () => {
    startSessionTimer();
    setRemaining(null);
  };

  if (!user || remaining === null) return null;

  return (
    <div
      role="alert"
      data-testid="session-timeout-banner"
      className="fixed bottom-4 right-4 z-[100] bg-amber-50 border border-amber-300 rounded-lg shadow-xl p-4 max-w-sm flex items-start gap-3"
    >
      <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
        <Clock className="w-4 h-4 text-amber-700" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold text-amber-900">Your session is about to expire</p>
        <p className="text-xs text-amber-800 mt-0.5">
          You'll be signed out in <span className="font-semibold tabular-nums">{formatRemaining(remaining)}</span>.
        </p>
        <Button
          onClick={stayIn}
          data-testid="session-stay-in-btn"
          size="sm"
          className="mt-3 bg-amber-600 hover:bg-amber-700 text-white rounded-sm h-8 text-xs"
        >
          Stay Signed In
        </Button>
      </div>
    </div>
  );
}
